/**
 * Хуки для получения конфига чат-моделей.
 * Динамические данные из usePricingStore, fallback на статику из config/models.ts
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePricingStore, getChatModelsSync } from '../stores/usePricingStore';
import { CHAT_MODELS_CONFIG } from '../config/models';
import type { ModelConfig } from '../types';

/**
 * Хук: массивы моделей по провайдерам (совместим с CHAT_MODELS_CONFIG)
 * Формат: { chatgpt: ModelConfig[], gemini: ModelConfig[], ... }
 */
export function useChatModelsConfig(): Record<string, ModelConfig[]> {
  const chatModels = usePricingStore(s => s.chatModels);

  return useMemo(() => {
    if (!chatModels) return CHAT_MODELS_CONFIG;

    const result: Record<string, ModelConfig[]> = {};
    for (const [key, provider] of Object.entries(chatModels)) {
      result[key] = provider.models.map(m => ({
        value: m.value,
        text: m.text,
        tagline: m.tagline,
        supportsVision: m.supportsVision,
        capabilities: m.capabilities,
        requiredTier: m.requiredTier,
      }));
    }
    return result;
  }, [chatModels]);
}

/**
 * Хук: метаданные провайдеров (displayName, description)
 */
export function useChatProvidersMeta(): Record<string, { displayName: string; description: string }> {
  const { t } = useTranslation('chat');
  const chatModels = usePricingStore(s => s.chatModels);

  return useMemo(() => {
    if (!chatModels) {
      const result: Record<string, { displayName: string; description: string }> = {};
      for (const [key, meta] of Object.entries(FALLBACK_PROVIDERS)) {
        result[key] = {
          displayName: meta.displayName,
          description: t(meta.descriptionKey),
        };
      }
      return result;
    }

    const result: Record<string, { displayName: string; description: string }> = {};
    for (const [key, provider] of Object.entries(chatModels)) {
      result[key] = {
        displayName: provider.displayName,
        description: provider.description,
      };
    }
    return result;
  }, [chatModels, t]);
}

/**
 * Синхронный геттер: массивы моделей по провайдерам (для вне React)
 */
export function getChatModelsConfigSync(): Record<string, ModelConfig[]> {
  const chatModels = getChatModelsSync();
  if (!chatModels) return CHAT_MODELS_CONFIG;

  const result: Record<string, ModelConfig[]> = {};
  for (const [key, provider] of Object.entries(chatModels)) {
    result[key] = provider.models.map(m => ({
      value: m.value,
      text: m.text,
      tagline: m.tagline,
      supportsVision: m.supportsVision,
      capabilities: m.capabilities,
      requiredTier: m.requiredTier,
      badges: m.badges as ModelConfig['badges'],
    }));
  }
  return result;
}

const FALLBACK_PROVIDERS: Record<string, { displayName: string; descriptionKey: string }> = {
  gemini: { displayName: 'Gemini', descriptionKey: 'modelSelector.providerDesc.gemini' },
  chatgpt: { displayName: 'ChatGPT', descriptionKey: 'modelSelector.providerDesc.chatgpt' },
  claude: { displayName: 'Claude', descriptionKey: 'modelSelector.providerDesc.claude' },
  deepseek: { displayName: 'DeepSeek', descriptionKey: 'modelSelector.providerDesc.deepseek' },
  grok: { displayName: 'Grok', descriptionKey: 'modelSelector.providerDesc.grok' },
};
